/**
 * 공지사항 관리 타입 정의
 */

/** 대상 타입 */
export type TargetType = 'A' | 'S' | 'SR' | 'R'; // A: 전체, S: 협력사, SR: 협력사+라이더, R: 라이더

/** 대상 범위 */
export type TargetScope = 'ALL' | 'INDIVIDUAL'; // ALL: 전체, INDIVIDUAL: 개별

/** 작성자 타입 */
export type WriterType = 'C' | 'S'; // C: 본사, S: 협력사

/** 공지사항 DTO */
export interface NoticeDto {
    noticeId: number;
    title: string;
    content: string;
    targetType: TargetType;
    targetTypeName: string;
    targetScope: TargetScope;
    targetScopeName: string;
    targetIds?: string[];
    writerId: string;
    writerType: WriterType;
    writerTypeName: string;
    suppId?: string;
    startDate: string;
    endDate?: string;
    popupYn: string;
    urgentYn: string;
    useYn: string;
    isRead?: boolean;
    insDate: string;
    insUser: string;
    updDate?: string;
    updUser?: string;
}

/** 공지사항 목록 응답 DTO */
export interface GetNoticeListResponseDto {
    notices: NoticeDto[];
    totalCount: number;
}

/** 공지사항 상세 응답 DTO */
export interface GetNoticeDetailResponseDto {
    notice: NoticeDto;
    success: boolean;
    message: string;
}

/** 공지사항 등록 요청 DTO */
export interface CreateNoticeRequestDto {
    title: string;
    content: string;
    targetType: TargetType;
    targetScope: TargetScope;
    targetIds?: string[];
    startDate?: string;
    endDate?: string;
    popupYn?: string;
    urgentYn?: string;
    insUser?: string;
}

/** 공지사항 수정 요청 DTO */
export interface UpdateNoticeRequestDto {
    noticeId: number;
    title?: string;
    content?: string;
    targetType?: TargetType;
    targetScope?: TargetScope;
    targetIds?: string[];
    startDate?: string;
    endDate?: string;
    popupYn?: string;
    urgentYn?: string;
    useYn?: string;
    updUser?: string;
}

/** 공지사항 처리 응답 DTO */
export interface NoticeResponseDto {
    noticeId: number;
    success: boolean;
    message: string;
}

/** 도메인 모델 - 공지사항 */
export interface Notice {
    noticeId: number;
    title: string;
    content: string;
    targetType: TargetType;
    targetTypeName: string;
    targetScope: TargetScope;
    targetScopeName: string;
    targetIds?: string[];
    writerId: string;
    writerType: WriterType;
    writerTypeName: string;
    suppId?: string;
    startDate: Date | null;
    endDate: Date | null;
    popupYn: string;
    urgentYn: string;
    useYn: string;
    isRead?: boolean;
    insDate: Date | null;
    insUser: string;
    updDate: Date | null;
    updUser?: string;
}

/**
 * 날짜 파싱 헬퍼 함수
 */
const parseDate = (value: string | number[] | null | undefined): Date | null => {
    if (!value) return null;

    if (Array.isArray(value)) {
        const [year, month, day, hour = 0, minute = 0, second = 0] = value;
        return new Date(year, month - 1, day, hour, minute, second);
    }

    if (typeof value === 'string') {
        const parsed = new Date(value);
        if (!isNaN(parsed.getTime())) {
            return parsed;
        }
    }

    return null;
};

/** DTO → Domain 변환 */
export const noticeFromDto = (dto: NoticeDto): Notice => ({
    noticeId: dto.noticeId,
    title: dto.title,
    content: dto.content,
    targetType: dto.targetType,
    targetTypeName: dto.targetTypeName,
    targetScope: dto.targetScope,
    targetScopeName: dto.targetScopeName,
    targetIds: dto.targetIds,
    writerId: dto.writerId,
    writerType: dto.writerType,
    writerTypeName: dto.writerTypeName,
    suppId: dto.suppId,
    startDate: parseDate(dto.startDate as unknown as string | number[]),
    endDate: parseDate(dto.endDate as unknown as string | number[]),
    popupYn: dto.popupYn,
    urgentYn: dto.urgentYn ?? 'N',
    useYn: dto.useYn,
    isRead: dto.isRead,
    insDate: parseDate(dto.insDate as unknown as string | number[]),
    insUser: dto.insUser,
    updDate: parseDate(dto.updDate as unknown as string | number[]),
    updUser: dto.updUser,
});

/** 공지사항 목록 DTO → Domain 변환 */
export const noticeListFromDto = (dto: GetNoticeListResponseDto): Notice[] =>
    (dto.notices ?? []).map(noticeFromDto);

/** 대상 타입 옵션 */
export const TARGET_TYPE_OPTIONS = [
    { value: 'A', label: '전체 (협력사 + 라이더)' },
    { value: 'S', label: '협력사' },
    { value: 'SR', label: '협력사 + 라이더' },
    { value: 'R', label: '라이더' },
];

/** 대상 범위 옵션 */
export const TARGET_SCOPE_OPTIONS = [
    { value: 'ALL', label: '전체' },
    { value: 'INDIVIDUAL', label: '개별 선택' },
];

/** 팝업 여부 옵션 */
export const POPUP_YN_OPTIONS = [
    { value: 'Y', label: '표시' },
    { value: 'N', label: '미표시' },
];

/** 사용 여부 옵션 */
export const USE_YN_OPTIONS = [
    { value: 'Y', label: '사용' },
    { value: 'N', label: '미사용' },
];

/** 긴급 공지 여부 옵션 */
export const URGENT_YN_OPTIONS = [
    { value: 'N', label: '일반' },
    { value: 'Y', label: '긴급' },
];
